import {
  Box,
  CircularProgress,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  AppFormikButtonTiles,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import EditIcon from '@mui/icons-material/Edit';
import { ReactComponent as Trash } from 'assets/images/ic_trash.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DetailView from './components/DetailView';
import { useEffect, useState } from 'react';
import { IRiskRegister } from '../List';
import { RiskRegisterService } from 'services/RiskRegisterService';
import { Toast, Utils } from 'shared/helpers';
import LinkedAISystems from './components/LinkedAISystems';
import LinkedDatasets from './components/LinkedDatasets';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RiskRegisterComments from './components/Comments';
import CommentsSection from 'modules/ai-inventory/ai-system/ai-systems-detail/components/CommentsSection';
import { EntityType } from 'shared/enums';
import AuditTrail from 'modules/ai-inventory/datasets/records-dataset/components/AuditTrail';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';
import useSize from 'shared/components/Hooks';

const Main: React.FC = () => {
  const { riskId } = useParams();
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [riskDetail, setRiskDetail] = useState<IRiskRegister>();
  const [openDeleteRisk, setOpenDeleteRisk] = useState(false);
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const { isDesktop, isMobile } = useSize();

  // API Calls
  const loadRiskDetail = (riskId: string): void => {
    setIsLoadingDetail(typeof riskDetail !== 'object');
    RiskRegisterService.detail(riskId)
      .then((response) => {
        const { data } = response.data;
        setRiskDetail(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        navigate(-1);
      })
      .finally(() => setIsLoadingDetail(false));
  };

  const handleCloseDeleteRisk = (): void => {
    setOpenDeleteRisk(false);
  };

  const handleDeleteRisk = (id: any): void => {
    setIsDeleting(true);
    RiskRegisterService.deleteRisk(id)
      .then(() => {
        Toast.showSuccessMessage(`Risk deleted successfully`);
        setOpenDeleteRisk(false);
        navigate(-1);
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      })
      .finally(() => setIsDeleting(false));
  };

  // Hooks
  useEffect(() => {
    if (riskId) {
      loadRiskDetail(riskId);
    }
  }, [riskId]);

  // UI Elements
  return (
    <>
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && riskDetail && (
        <Box
          sx={{
            padding: '35px',
          }}
        >
          <Stack
            justifyContent="space-between"
            alignItems="center"
            direction="row"
          >
            <Stack
              spacing={2}
              direction={isDesktop ? 'row' : 'column'}
              alignItems="center"
            >
              <AppPrimaryButtonOutlined
                startIcon={<ChevronLeftIcon />}
                className={isMobile ? 'btn-extra-small' : 'btn-small'}
                title="Back"
                onClick={() => navigate('/risk-register')}
              />
              <Typography variant="h3">{riskDetail?.riskTitle}</Typography>
            </Stack>
            <Stack
              spacing={2}
              direction={isDesktop ? 'row' : 'column'}
              alignItems="center"
            >
              {orgACLConfig.riskManagement?.includes(
                'riskManagement.riskRegister.view.editRisk'
              ) && (
                <AppPrimaryButton
                  onClick={() =>
                    navigate(`/risk-register/edit/${riskDetail?._id}`)
                  }
                  className={isMobile ? 'btn-extra-small' : 'btn-small'}
                  title="Edit Risk"
                  startIcon={<EditIcon />}
                />
              )}
              {orgACLConfig.riskManagement?.includes(
                'riskManagement.riskRegister.view.deleteRisk'
              ) && (
                <AppPrimaryButton
                  title="Delete Risk"
                  className={`${
                    isMobile ? 'btn-extra-small' : 'btn-small'
                  } btn-danger`}
                  isLoading={isDeleting}
                  loadingText="Deleting"
                  startIcon={
                    <SvgIcon
                      component={Trash}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="white"
                    />
                  }
                  onClick={() => {
                    setOpenDeleteRisk(true);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Formik
            initialValues={{ status: [true, false] }}
            onSubmit={() => console.log('test')}
          >
            {({ values }) => (
              <>
                <AppFormikButtonTiles
                  variant="outlined"
                  name="status"
                  size="small"
                  label=""
                  disabled={false}
                  multichoice={false}
                  className="app-group-button my-5"
                  buttons={[
                    {
                      value: 'Details',
                      tooltip:
                        'View and edit risk core metadata, impact levels, owner assignments, and mitigation status.',
                    },
                    {
                      value: 'History',
                      tooltip:
                        'Track the audit trail of past risk evaluations, status changes, and modifications.',
                    },
                    {
                      value: 'Linked AI Systems',
                      tooltip:
                        'Review and manage all AI systems, models, and deployments exposed to or impacted by this risk.',
                    },
                    {
                      value: 'Linked Datasets',
                      tooltip:
                        'Datasets associated with or subject to this risk.',
                    },
                    {
                      value: 'Comments',
                      tooltip:
                        'Collaborate with team members, add review notes, and track internal discussions regarding this risk item.',
                    },
                  ]}
                  outerLabel
                />

                <>
                  {values.status[0] && <DetailView riskDetail={riskDetail} />}
                  {values.status[1] && (
                    <>
                      <AuditTrail
                        title="History"
                        entityType={EntityType.RISK_REGISTER}
                      />

                      {false && (
                        <EmptyPlaceholder
                          icon={<ScheduleIcon />}
                          title="No history yet."
                          message=""
                        />
                      )}
                    </>
                  )}
                  {values.status[2] && (
                    <LinkedAISystems
                      linkedSystems={riskDetail.aisystemId || []}
                      riskId={`${riskDetail._id}`}
                      reloadDetail={() => {
                        loadRiskDetail(`${riskId}`);
                      }}
                    />
                  )}
                  {values.status[3] && (
                    <LinkedDatasets
                      linkedDatasets={riskDetail.datasetId || []}
                      riskId={`${riskDetail._id}`}
                      reloadDetail={() => {
                        loadRiskDetail(`${riskId}`);
                      }}
                    />
                  )}
                  {values.status[4] && (
                    <>
                      <CommentsSection entityType={EntityType.RISK_REGISTER} />
                      {false && <RiskRegisterComments />}
                    </>
                  )}
                </>
              </>
            )}
          </Formik>
        </Box>
      )}

      <ConfirmDialog
        title="Delete Risk"
        subTitle="This action cannot be undone. This will permanently delete the risk and all associated records."
        showConfirm={openDeleteRisk}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteRisk();
          handleDeleteRisk(riskId);
        }}
        onCancel={handleCloseDeleteRisk}
        onCloseDialog={handleCloseDeleteRisk}
      />
    </>
  );
};

export default Main;
